// Press Releases - Public page showing company announcements
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '../utils/api';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { FileText, Calendar, ArrowRight } from 'lucide-react';

export function PressReleases() {
  const [pressReleases, setPressReleases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPressReleases();
  }, []);

  async function loadPressReleases() {
    try {
      const response = await api.request('/seo/press-releases/public');
      setPressReleases(response.press_releases || []);
    } catch (err) {
      console.error('Failed to load press releases:', err);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }

  return (
    <>
      <Helmet>
        <title>Press Releases | UPost - AI Writing Platform News</title>
        <meta name="description" content="Latest news and announcements from UPost. Stay updated on new features, milestones, and company updates." />
        <meta property="og:title" content="UPost Press Releases - Company News & Updates" />
        <meta property="og:description" content="Latest press releases and announcements from UPost, the AI-powered writing platform for authors." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Header */}
        <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-2">
                <Logo className="w-10 h-10" />
                <span className="text-xl font-bold">UPost</span>
              </Link>
              <nav className="hidden md:flex items-center space-x-6">
                <Link to="/blog" className="text-gray-600 hover:text-gray-900">Blog</Link>
                <Link to="/press" className="text-purple-600 font-medium">Press</Link>
                <Link to="/pricing" className="text-gray-600 hover:text-gray-900">Pricing</Link>
                <Link to="/login" className="text-gray-600 hover:text-gray-900">Log In</Link>
                <Link to="/register">
                  <Button>Sign Up</Button>
                </Link>
              </nav>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-purple-100 text-purple-800 hover:bg-purple-100">
              Company News
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Press Releases
            </h1>
            <p className="text-xl text-gray-600">
              Latest news, announcements, and updates from UPost
            </p>
          </div>
        </section>

        {/* Press Releases List */}
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : pressReleases.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600">No press releases yet. Check back soon!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {pressReleases.map((pr) => (
                  <Card key={pr.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs">
                              {pr.category || 'Technology'}
                            </Badge>
                            <div className="flex items-center text-sm text-gray-500">
                              <Calendar className="w-4 h-4 mr-1" />
                              {formatDate(pr.distributed_at || pr.created_at)}
                            </div>
                          </div>
                          <CardTitle className="text-2xl mb-2 hover:text-purple-600 transition-colors">
                            <Link to={`/press/${pr.id}`}>
                              {pr.title}
                            </Link>
                          </CardTitle>
                          <p className="text-gray-600 line-clamp-2">
                            {pr.summary}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Link to={`/press/${pr.id}`}>
                        <Button variant="outline" className="group">
                          Read Full Release
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              Want to stay updated?
            </h2>
            <p className="text-xl mb-8 text-purple-100">
              Follow our blog for writing tips, feature updates, and industry insights
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/blog">
                <Button size="lg" variant="secondary">
                  Read Our Blog
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
                  Start Writing Free
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t bg-white py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center text-gray-600">
            <div className="flex items-center justify-center space-x-6 mb-4">
              <Link to="/blog" className="hover:text-gray-900">Blog</Link>
              <Link to="/press" className="hover:text-gray-900">Press</Link>
              <Link to="/pricing" className="hover:text-gray-900">Pricing</Link>
              <Link to="/affiliates" className="hover:text-gray-900">Affiliates</Link>
            </div>
            <p className="text-sm">
              © {new Date().getFullYear()} UPost. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
